import React, { useContext, useEffect, useState } from 'react';
import { Form, Input, Button, Row, Col, Breadcrumb, Select, Card, Grid, message, Radio } from 'antd';
import { RightOutlined, UserOutlined, PhoneOutlined, MailOutlined, HomeOutlined, LockOutlined, SaveOutlined, EditOutlined, InfoCircleOutlined, BgColorsOutlined, SunOutlined, MoonOutlined, DesktopOutlined, GlobalOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { UserService } from '../../common/services';
import { AuthContext } from '../../common/constants';
import { toast } from 'react-toastify';
import { rulePhoneCI, ruleEmail, ruleRequired, normalizeUpper, normalizeLowerTrim } from '../../common/validators';
import { useAppTheme } from '../../theme/AppThemeProvider';
import { useLocale } from '../../common/i18n';

const Profile = () => {
  const [form] = Form.useForm();
  const { currentUser, setCurrentUser } = useContext(AuthContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const { mode: themeMode, setMode: setThemeMode } = useAppTheme();
  const { locale, setLocale } = useLocale();

  useEffect(() => {
    if (currentUser) {
      form.setFieldsValue({
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        phone: currentUser.phone?.replace('+225', '') || '',
        email: currentUser.email,
        residence: currentUser.residence,
      });
    }
  }, [currentUser, form]);

  const handleSubmit = async (values) => {
    if (values.password) {
      if (!values.currentPassword) {
        return toast.error("Veuillez saisir votre mot de passe actuel pour le modifier");
      }
      if (values.password !== values.confirmPassword) {
        return toast.error("Les mots de passe ne correspondent pas");
      }
      if (values.password.length < 6) {
        return toast.error("Le nouveau mot de passe doit faire au moins 6 caractères");
      }
    }
    const { confirmPassword, currentPassword, password, ...rest } = values;
    const finalValues = { ...rest, phone: `+225${values.phone}` };
    // N'inclure le password que s'il est modifié, et joindre l'ancien
    if (password) {
      finalValues.password = password;
      finalValues.currentPassword = currentPassword;
    }
    try {
      setIsSubmitting(true);
      const response = await UserService.updateProfile(currentUser._id, finalValues);
      const updatedUser = response?.data || response;
      if (updatedUser && updatedUser._id) {
        const newUser = { ...currentUser, ...updatedUser };
        setCurrentUser(newUser);
        localStorage.setItem('currentUser', JSON.stringify(newUser));
      }
      toast.success('Profil mis à jour avec succès');
      // Vider les champs de password après succès
      form.setFieldsValue({ currentPassword: '', password: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la mise à jour');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: isMobile ? 12 : 24, paddingRight: isMobile ? 12 : 24 }}>
        <Breadcrumb separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard" style={{ color: '#1A2B5F' }}>Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item><span style={{ color: '#595959' }}>Mon Profil</span></Breadcrumb.Item>
        </Breadcrumb>

        <Form form={form} layout="vertical" onFinish={handleSubmit} validateTrigger={['onChange', 'onBlur']} size={isMobile ? 'small' : 'middle'}>
          <Row gutter={[24, 16]}>
            <Col xs={24} lg={12}>
              <Card style={{ borderRadius: 12, height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                title={<span><UserOutlined style={{ color: '#C8102E', marginRight: 8 }} />Informations personnelles</span>}
                headStyle={{ borderBottom: '2px solid #f0f0f0' }}>
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="firstName" label="Prénom" rules={ruleRequired('prénom')} normalize={normalizeUpper}>
                      <Input prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} style={{ borderRadius: 8 }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="lastName" label="Nom" rules={ruleRequired('nom')} normalize={normalizeUpper}>
                      <Input prefix={<EditOutlined style={{ color: '#bfbfbf' }} />} style={{ borderRadius: 8 }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="phone" label="Téléphone" rules={rulePhoneCI({ label: 'téléphone' })}>
                  <Input addonBefore="+225" maxLength={10} style={{ borderRadius: 8 }} />
                </Form.Item>
                <Form.Item name="email" label="Email" rules={ruleEmail()} normalize={normalizeLowerTrim}>
                  <Input type="email" prefix={<MailOutlined style={{ color: '#bfbfbf' }} />} style={{ borderRadius: 8 }} />
                </Form.Item>
                <Form.Item name="residence" label="Résidence">
                  <Input prefix={<HomeOutlined style={{ color: '#bfbfbf' }} />} style={{ borderRadius: 8 }} />
                </Form.Item>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card style={{ borderRadius: 12, marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                title={<span><LockOutlined style={{ color: '#C8102E', marginRight: 8 }} />Sécurité</span>}
                headStyle={{ borderBottom: '2px solid #f0f0f0' }}>
                <Form.Item shouldUpdate={(prev, cur) => prev.password !== cur.password} noStyle>
                  {({ getFieldValue }) => {
                    const newPwd = getFieldValue('password');
                    const required = !!newPwd;
                    return (
                      <Form.Item
                        name="currentPassword"
                        label="Mot de passe actuel"
                        extra="Requis uniquement si vous changez votre mot de passe"
                        rules={required ? [{ required: true, message: 'Saisissez votre mot de passe actuel pour confirmer le changement' }] : []}
                      >
                        <Input.Password
                          prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                          style={{ borderRadius: 8 }}
                          autoComplete="current-password"
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
                <Form.Item
                  name="password"
                  label="Nouveau mot de passe"
                  extra="Laissez vide pour conserver le mot de passe actuel. Minimum 6 caractères."
                  rules={[{ min: 6, message: 'Minimum 6 caractères' }]}
                >
                  <Input.Password prefix={<LockOutlined style={{ color: '#bfbfbf' }} />} style={{ borderRadius: 8 }} autoComplete="new-password" />
                </Form.Item>
                <Form.Item name="confirmPassword" label="Confirmer le nouveau mot de passe" dependencies={['password']}
                  rules={[({ getFieldValue }) => ({ validator(_, value) { if (!value || getFieldValue('password') === value) return Promise.resolve(); return Promise.reject(new Error('Les mots de passe ne correspondent pas')); } })]}>
                  <Input.Password prefix={<LockOutlined style={{ color: '#bfbfbf' }} />} style={{ borderRadius: 8 }} autoComplete="new-password" />
                </Form.Item>
              </Card>

              <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                title={<span><InfoCircleOutlined style={{ color: '#C8102E', marginRight: 8 }} />Informations du compte</span>}
                headStyle={{ borderBottom: '2px solid #f0f0f0' }}>
                <Row gutter={[16, 8]}>
                  <Col span={12}><div style={{ color: '#8c8c8c', fontSize: 12 }}>ID</div><div style={{ fontWeight: 500, fontSize: 13, fontFamily: 'monospace' }}>{currentUser?._id?.slice(-8) || '-'}</div></Col>
                  <Col span={12}><div style={{ color: '#8c8c8c', fontSize: 12 }}>Role</div><div style={{ fontWeight: 500, fontSize: 13 }}>{currentUser?.role || 'Administrateur'}</div></Col>
                  <Col span={12}><div style={{ color: '#8c8c8c', fontSize: 12 }}>Créé le</div><div style={{ fontWeight: 500, fontSize: 13 }}>{currentUser?.createdAt ? new Date(currentUser.createdAt).toLocaleDateString('fr-FR') : '-'}</div></Col>
                  <Col span={12}><div style={{ color: '#8c8c8c', fontSize: 12 }}>Derniere connexion</div><div style={{ fontWeight: 500, fontSize: 13 }}>{currentUser?.lastLogin ? new Date(currentUser.lastLogin).toLocaleDateString('fr-FR') : "Aujourd'hui"}</div></Col>
                </Row>
              </Card>

              <Card style={{ borderRadius: 12, marginTop: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                title={<span><BgColorsOutlined style={{ color: '#C8102E', marginRight: 8 }} />Apparence</span>}
                headStyle={{ borderBottom: '2px solid #f0f0f0' }}>
                <div style={{ marginBottom: 12, color: 'var(--cap-text-secondary, #595959)', fontSize: 13 }}>
                  Choisissez le thème de l'interface (préférence sauvegardée pour ce navigateur).
                </div>
                <Radio.Group value={themeMode} onChange={(e) => setThemeMode(e.target.value)} buttonStyle="solid">
                  <Radio.Button value="light"><SunOutlined style={{ marginRight: 6 }} />Clair</Radio.Button>
                  <Radio.Button value="dark"><MoonOutlined style={{ marginRight: 6 }} />Sombre</Radio.Button>
                  <Radio.Button value="system"><DesktopOutlined style={{ marginRight: 6 }} />Auto (système)</Radio.Button>
                </Radio.Group>
              </Card>

              <Card style={{ borderRadius: 12, marginTop: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                title={<span><GlobalOutlined style={{ color: '#C8102E', marginRight: 8 }} />Langue</span>}
                headStyle={{ borderBottom: '2px solid #f0f0f0' }}>
                <div style={{ marginBottom: 12, color: 'var(--cap-text-secondary, #595959)', fontSize: 13 }}>
                  Choisissez la langue de l'interface.
                </div>
                <Radio.Group value={locale} onChange={(e) => setLocale(e.target.value)} buttonStyle="solid">
                  <Radio.Button value="fr">🇫🇷 Français</Radio.Button>
                  <Radio.Button value="en">🇬🇧 English</Radio.Button>
                </Radio.Group>
              </Card>
            </Col>
          </Row>

          <Card style={{ borderRadius: 12, marginTop: 24 }} bodyStyle={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end', gap: 12, flexDirection: isMobile ? 'column' : 'row' }}>
              <Button onClick={() => form.resetFields()} style={{ borderRadius: 8, width: isMobile ? '100%' : 'auto' }}>Réinitialiser</Button>
              <Button type="primary" htmlType="submit" loading={isSubmitting} icon={<SaveOutlined />}
                className="btn-green" style={{ borderRadius: 8, width: isMobile ? '100%' : 'auto', minWidth: 200 }}>
                {isSubmitting ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </Button>
            </div>
          </Card>
        </Form>
      </div>
    </div>
  );
};

export default Profile;
